import { t } from "@lingui/macro"
import { makeAutoObservable } from "mobx"
import React from "react"

import { ChatbotAdminService, KnowledgeHubAdminService } from "src/api"
import { CustomDataHelper } from "src/api/_custom/services/DataHelper"
import { Channel } from "src/channel"
import { loads } from "src/channel/utils"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { createLoadingKeys } from "src/lib/loading"
import { Pagination } from "src/lib/pagination"
import { reportError } from "src/lib/report"
import {
    IAdvanceQueryModel,
    IGenericSingleSelectOptions,
} from "src/types/data-grid-pro"
import { IKnowledgeItem } from "src/views/knowledge-base/pages/list/types"

export class KnowledgeBaseListStore {
    //#region initialization
    static Context = React.createContext<KnowledgeBaseListStore | null>(null)
    static LoadingKeys = createLoadingKeys("loading")
    knowledgeItems = new Pagination(
        async (params) => {
            const response =
                await KnowledgeHubAdminService.postV1AdminKnowledgebaseKnowledgeItemsList(
                    {
                        request: {
                            filter: params.advanceQuery,
                            page_number: params.page,
                            page_size: params.pageSize,
                            ...CustomDataHelper.PrepareVisibilityData(this),
                        },
                    },
                )

            const items: IKnowledgeItem[] = (
                response.knowledge_items ?? []
            ).map((item) => ({
                id: item.id ?? -1,
                title: item.title ?? "",
                access_group_id: item.access_group_id ?? -1,
                description: item.description ?? "",
                segments: item.segments ?? [],
                segment_names: item.segment_names ?? [],
                tags: item.tags ?? [],
                agents: item.agents ?? [],
                agent_types: item.agent_types ?? [],
                content: item.content ?? "",
                knowledge_item_type: item.knowledge_item_type ?? undefined,
                access_type: item.access_type ?? "NONE",
            }))

            return {
                items,
                count: response.total_count ?? 0,
            }
        },
        {
            loadingKey: KnowledgeBaseListStore.LoadingKeys.loading,
        },
    )
    private repositoryUpdatesListenerDisposer?: () => void

    constructor() {
        makeAutoObservable(this)
    }

    _segments: number[] = []

    //#region getters
    get segments() {
        return this._segments
    }

    _agentAccessTypes: IGenericSingleSelectOptions[] = []
    //#endregion

    get agentAccessTypes() {
        return this._agentAccessTypes
    }

    _accessGroupId: number = DEFAULT_ACCESS_GROUP.id

    private get accessGroupId() {
        return this._accessGroupId
    }
    //#endregion

    setAgentAccessTypes = (types: IGenericSingleSelectOptions[]) => {
        this._agentAccessTypes = types
    }

    //#region store operations
    async init(accessGroupId: number, advanceQuery?: IAdvanceQueryModel) {
        this.listenToRepositoryUpdates()

        this.setAccessGroup(accessGroupId)
        try {
            await this.knowledgeItems.loadInitialPage(advanceQuery)
            await this.fetchAgentTypes()
        } catch (error) {
            reportError(t`knowledge-base.init-fail`, error)
        }
    }

    dispose() {
        this.repositoryUpdatesListenerDisposer?.()
    }
    //#endregion

    @loads(() => KnowledgeBaseListStore.LoadingKeys.loading)
    async fetchAgentTypes() {
        try {
            const configDefinition =
                await ChatbotAdminService.getV1AdminChatbotAgentConfigConfigDefinition(
                    {
                        space: "knowledgebase",
                    },
                )

            if (
                configDefinition.definition != null &&
                configDefinition.definition.length > 0
            ) {
                const types = configDefinition.definition.map((field) => ({
                    value: field.config_definition_id ?? 0,
                    label: field.name ?? "",
                }))

                this.setAgentAccessTypes(types)
            }
        } catch (error) {
            reportError(t`knowledge-item-modal.load-agent-types-fail`, error)
        }
    }

    async loadSegments(segments: number[], advanceQuery?: IAdvanceQueryModel) {
        this.setSegments(segments)
        await this.knowledgeItems.loadInitialPage(advanceQuery)
    }

    deleteItem = async (id: number) => {
        try {
            await KnowledgeHubAdminService.deleteV1AdminKnowledgebaseKnowledgeItems(
                {
                    knowledgeItemId: id,
                },
            )
        } catch (error) {
            reportError(t`template-features.delete-item-fail`, error)
        } finally {
            await this.knowledgeItems.reload()
        }
    }

    async query(advanceQuery: IAdvanceQueryModel) {
        await this.knowledgeItems.loadAdvanceQuery(advanceQuery)
    }

    //#region setters
    private setSegments(segments: number[]) {
        this._segments = segments
    }

    private setAccessGroup(accessGroupId: number) {
        this._accessGroupId = accessGroupId
    }

    private listenToRepositoryUpdates() {
        this.repositoryUpdatesListenerDisposer = Channel.addListener(
            async (event) => {
                if (
                    event.name === "repository/updated" &&
                    event.payload.repository === "knowledge-base"
                ) {
                    await this.knowledgeItems.reload()
                }
            },
        )
    }
    //#endregion
}
